import { bankStatementsService } from '@/api/bank-statements.service';
import {
  BankStatementAction,
  BankStatementListStatus,
  type BankStatementEntryDto,
  type BankStatementEntryPageDto,
  type ConfirmBankStatementActionDto,
  type BankStatementWebhookPayloadDto,
} from '@carvia/ros-client-types';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/vue-query';
import { computed, type MaybeRefOrGetter, toValue } from 'vue';

const QUERY_KEY = 'bank-statements';
const PAGE_SIZE = 40;

export const useBankStatementAccounts = (enabled: MaybeRefOrGetter<boolean> = true) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'accounts'],
    queryFn: () => bankStatementsService.listAccounts(),
    enabled: computed(() => toValue(enabled)),
  });
};

export const useBankStatementEntries = (
  status: MaybeRefOrGetter<BankStatementListStatus>,
  accountIban: MaybeRefOrGetter<string>,
  legalEntityId: MaybeRefOrGetter<string | null>,
  dateRange: MaybeRefOrGetter<{ startDate?: string; endDate?: string }>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useInfiniteQuery({
    queryKey: computed(() => [
      QUERY_KEY,
      'entries',
      toValue(status),
      toValue(accountIban),
      toValue(legalEntityId),
      toValue(dateRange),
    ]),
    queryFn: ({ pageParam }) =>
      bankStatementsService.getEntries({
        cursor: pageParam ?? undefined,
        limit: PAGE_SIZE,
        status: toValue(status),
        accountIban: toValue(accountIban) || undefined,
        legalEntityId: toValue(legalEntityId) ?? undefined,
        ...toValue(dateRange),
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: computed(() => toValue(enabled)),
  });
};

export const useBankStatementOpenAmounts = (
  entryId: MaybeRefOrGetter<string | null>,
  search: MaybeRefOrGetter<string>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: computed(() => [QUERY_KEY, 'open-amounts', toValue(entryId), toValue(search)]),
    queryFn: () => bankStatementsService.getOpenAmounts(toValue(entryId)!, toValue(search)),
    enabled: computed(() => toValue(enabled) && !!toValue(entryId)),
  });
};

export const useBankStatementPayerOpenAmounts = (
  entryId: MaybeRefOrGetter<string | null>,
  payerUserId: MaybeRefOrGetter<string | null>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: computed(() => [
      QUERY_KEY,
      'payer-open-amounts',
      toValue(entryId),
      toValue(payerUserId),
    ]),
    queryFn: () =>
      bankStatementsService.getPayerOpenAmounts(toValue(entryId)!, toValue(payerUserId)!),
    enabled: computed(() => toValue(enabled) && !!toValue(entryId) && !!toValue(payerUserId)),
  });
};

export const useIngestBankStatements = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: BankStatementWebhookPayloadDto) => bankStatementsService.ingest(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
};

export const useConfirmBankStatementAction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, body }: { entryId: string; body: ConfirmBankStatementActionDto }) =>
      bankStatementsService.confirm(entryId, body),
    onSuccess: (updated) => {
      dropOrReplaceConfirmedEntry(queryClient, updated);
    },
    // A failed relink may already have unlinked the entry; reload so a retry sees its real state.
    onError: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
};

type EntriesCache = InfiniteData<BankStatementEntryPageDto>;

function shouldEntryStayOnList(
  status: BankStatementListStatus | undefined,
  action: BankStatementAction | null,
): boolean {
  switch (status ?? BankStatementListStatus.UNMATCHED) {
    case BankStatementListStatus.UNMATCHED:
      return action == null;
    case BankStatementListStatus.IGNORED:
      return action === BankStatementAction.IGNORE;
    case BankStatementListStatus.CONFIRMED:
      return action != null && action !== BankStatementAction.IGNORE;
    default:
      return true;
  }
}

/** Safe without a refetch: the next page continues after the last loaded entry, not at an offset. */
function dropOrReplaceConfirmedEntry(
  queryClient: ReturnType<typeof useQueryClient>,
  updated: BankStatementEntryDto,
): void {
  const queries = queryClient.getQueriesData<EntriesCache>({ queryKey: [QUERY_KEY, 'entries'] });
  for (const [key, data] of queries) {
    if (!data) continue;
    const status = key[2] as BankStatementListStatus | undefined;
    const stays = shouldEntryStayOnList(status, updated.action);
    queryClient.setQueryData<EntriesCache>(key, {
      ...data,
      pages: data.pages.map((page) => {
        if (!page.items.some((item) => item.id === updated.id)) return page;
        const items = stays
          ? page.items.map((item) => (item.id === updated.id ? { ...item, ...updated } : item))
          : page.items.filter((item) => item.id !== updated.id);
        return { ...page, items };
      }),
    });
  }
}
